import React, { useEffect, useState } from 'react';
import { Form, Header, Input, Label, Segment, Table } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';

const MONTH_MIN = '2026-09';
const MONTH_MAX = '2028-09';

// 生成 2026-09 ~ 2028-09 的月份选项
function buildMonthOptions() {
  const options = [];
  const [minYear, minMonth] = MONTH_MIN.split('-').map(Number);
  const [maxYear, maxMonth] = MONTH_MAX.split('-').map(Number);
  let year = minYear;
  let month = minMonth;
  while (year < maxYear || (year === maxYear && month <= maxMonth)) {
    const value = `${year}-${String(month).padStart(2, '0')}`;
    options.push({ key: value, value, text: value });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return options;
}

const monthOptions = buildMonthOptions();

// 默认选上月；若落在区间外则退回区间首月
function defaultMonth() {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const value = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  if (value < MONTH_MIN) return MONTH_MIN;
  if (value > MONTH_MAX) return MONTH_MAX;
  return value;
}

// 分 → 元，保留两位
function fen2yuan(fen) {
  return ((fen || 0) / 100).toFixed(2);
}

// 元 → 分，解析失败返回 null
function yuan2fen(text) {
  const n = parseFloat(text);
  if (isNaN(n)) return null;
  return Math.round(n * 100);
}

const FinancialSummary = () => {
  const [month, setMonth] = useState(defaultMonth());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 可编辑项的本地草稿（元为单位的字符串）
  const [drafts, setDrafts] = useState({});

  const syncDrafts = (data) => {
    setDrafts({
      advance_opening: data.overrides.advance_opening ? fen2yuan(data.advance_opening_fen) : '',
      cost_with_tax: fen2yuan(data.cost_with_tax_fen),
      cost_ex_tax: data.overrides.cost_ex_tax ? fen2yuan(data.cost_ex_tax_fen) : '',
      invoiced_with_tax: data.overrides.invoiced_with_tax ? fen2yuan(data.invoiced_with_tax_fen) : '',
    });
  };

  const loadSummary = async (targetMonth) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/financial/summary?month=${targetMonth}`);
      const { success, message, data } = res.data;
      if (success) {
        setSummary(data);
        syncDrafts(data);
      } else {
        showError(message);
      }
    } catch (e) {
      showError('加载失败：' + (e.message || '未知错误'));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSummary(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const handleSave = async () => {
    const costWithTaxFen = yuan2fen(drafts.cost_with_tax) ?? 0;
    setSaving(true);
    try {
      const res = await API.put('/api/financial/input', {
        month,
        cost_with_tax_fen: costWithTaxFen,
        // 留空即清除覆盖，回退到系统自动计算值
        cost_ex_tax_fen: yuan2fen(drafts.cost_ex_tax),
        invoiced_with_tax_fen: yuan2fen(drafts.invoiced_with_tax),
        advance_opening_fen: yuan2fen(drafts.advance_opening),
      });
      const { success, message, data } = res.data;
      if (success) {
        setSummary(data);
        syncDrafts(data);
        showSuccess('已保存');
      } else {
        showError(message);
      }
    } catch (e) {
      showError('保存失败：' + (e.message || '未知错误'));
    }
    setSaving(false);
  };

  const setDraft = (key, value) => setDrafts((prev) => ({ ...prev, [key]: value }));

  // 分组小标题行
  const groupRow = (title) => (
    <Table.Row key={title}>
      <Table.Cell colSpan='4' style={{ background: '#f9fafb', fontWeight: 'bold' }}>
        {title}
      </Table.Cell>
    </Table.Row>
  );

  // 只读金额行
  const amountRow = (code, label, fen, note) => (
    <Table.Row key={code}>
      <Table.Cell collapsing>{code}</Table.Cell>
      <Table.Cell>{label}</Table.Cell>
      <Table.Cell textAlign='right'>{fen2yuan(fen)}</Table.Cell>
      <Table.Cell style={{ color: '#767676', fontSize: '0.9em' }}>{note}</Table.Cell>
    </Table.Row>
  );

  // 可编辑金额行；placeholder 展示系统默认值，留空即用默认值
  const editableRow = (code, label, draftKey, currentFen, note, overridden) => (
    <Table.Row key={code}>
      <Table.Cell collapsing>{code}</Table.Cell>
      <Table.Cell>
        {label}
        {overridden && (
          <Label size='mini' color='orange' style={{ marginLeft: 6 }}>
            手工
          </Label>
        )}
      </Table.Cell>
      <Table.Cell textAlign='right'>
        <Input
          size='mini'
          fluid
          transparent={false}
          value={drafts[draftKey] ?? ''}
          placeholder={fen2yuan(currentFen)}
          onChange={(e) => setDraft(draftKey, e.target.value)}
          style={{ maxWidth: 140 }}
        />
      </Table.Cell>
      <Table.Cell style={{ color: '#767676', fontSize: '0.9em' }}>{note}</Table.Cell>
    </Table.Row>
  );

  return (
    <Segment loading={loading}>
      <Header as='h3'>财务月报</Header>
      <Form>
        <Form.Group>
          <Form.Select
            fluid
            label='月份'
            width={4}
            options={monthOptions}
            value={month}
            onChange={(e, { value }) => setMonth(value)}
          />
          <Form.Button
            fluid
            label='操作'
            width={3}
            onClick={() => loadSummary(month)}
            loading={loading}
          >
            刷新
          </Form.Button>
          <Form.Button
            fluid
            label='&nbsp;'
            width={3}
            primary
            onClick={handleSave}
            loading={saving}
          >
            保存手填项
          </Form.Button>
        </Form.Group>
      </Form>

      {summary && (
        <Table basic compact size='small' celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>编号</Table.HeaderCell>
              <Table.HeaderCell>项目</Table.HeaderCell>
              <Table.HeaderCell textAlign='right'>金额（元）</Table.HeaderCell>
              <Table.HeaderCell>公式 / 说明</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groupRow('预收账款')}
            {editableRow('D4', '期初余额', 'advance_opening', summary.advance_opening_fen,
              '默认由上月期末余额结转，可手工调整', summary.overrides.advance_opening)}
            {amountRow('D5', '本期增加', summary.advance_increase_fen, '本月新增充值金额（含税），不含兑换券')}
            {amountRow('D6', '本期减少', summary.advance_decrease_fen, '本月使用金额（不含无偿赠送部分）含税')}
            {amountRow('D7', '期末余额', summary.advance_closing_fen, '=D4+D5-D6')}

            {groupRow('本期收入')}
            {amountRow('D8', '本期收入（含税）', summary.income_with_tax_fen, '=D6')}
            {amountRow('D9', '本期收入（不含税）', summary.income_ex_tax_fen, '=ROUND(D8/1.06,2)')}

            {groupRow('本期成本及费用')}
            {editableRow('D10', '本期成本（含税）', 'cost_with_tax', summary.cost_with_tax_fen,
              '手填', false)}
            {amountRow('D11', '本期营销费用（含税）', summary.marketing_with_tax_fen,
              '恒为 0（不填）')}
            {amountRow('D12', '合计（含税）', summary.cost_total_with_tax_fen, '=D10+D11')}
            {editableRow('D13', '本期成本（不含税）', 'cost_ex_tax', summary.cost_ex_tax_fen,
              '默认 =ROUND(D10/1.06,2)，可手工调整', summary.overrides.cost_ex_tax)}
            {amountRow('D14', '本期营销费用（不含税）', summary.marketing_ex_tax_fen, '=ROUND(D11/1.06,2)')}
            {amountRow('D15', '合计（不含税）', summary.cost_total_ex_tax_fen, '=D13+D14')}

            {groupRow('本期税务数据 - 未开票部分')}
            {amountRow('D16', '含税金额', summary.uninvoiced_with_tax_fen, '=D8')}
            {amountRow('D17', '不含税金额', summary.uninvoiced_ex_tax_fen, '=D9')}
            {amountRow('D18', '销项税额', summary.uninvoiced_tax_fen, '=D16-D17')}

            {groupRow('本期税务数据 - 本期实际开票部分')}
            {editableRow('D19', '含税金额', 'invoiced_with_tax', summary.invoiced_with_tax_fen,
              '默认取本月已开票的消费发票合计，可手工调整', summary.overrides.invoiced_with_tax)}
            {amountRow('D20', '不含税金额', summary.invoiced_ex_tax_fen, '=ROUND(D19/1.06,2)')}
            {amountRow('D21', '销项税额', summary.invoiced_tax_fen, '=D19-D20')}

            {groupRow('本期税务数据 - 本期税务系统申报数据')}
            {amountRow('D22', '含税金额', summary.declared_with_tax_fen, '=D16-D19')}
            {amountRow('D23', '不含税金额', summary.declared_ex_tax_fen, '=D17-D20')}
            {amountRow('D24', '销项税额', summary.declared_tax_fen, '=D18-D21')}
          </Table.Body>
        </Table>
      )}

      <p style={{ color: '#767676', fontSize: '0.9em' }}>
        可编辑项留空表示使用系统自动计算值。D10 无自动来源，需手填。
      </p>
    </Segment>
  );
};

export default FinancialSummary;
